import {
    Document,
    Page,
    View,
    Text,
    Image,
    StyleSheet,
    Font,
} from '@react-pdf/renderer';

import NotoSansDevanagariR from '../font/NotoSansDevanagari-Regular.ttf';
import NotoSansDevanagariB from '../font/NotoSansDevanagari-Bold.ttf';

// Font Registration
Font.register({
    family: 'NotoSansDevanagari',
    fonts: [
        { src: NotoSansDevanagariR, fontWeight: 'normal' },
        { src: NotoSansDevanagariB, fontWeight: 'bold' },
    ],
});

// Helpers
const formatDate = (date) => {
    try {
        const d = date instanceof Date ? date : new Date(date);
        if (isNaN(d)) return String(date);
        return d.toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
        });
    } catch {
        return String(date);
    }
};

const formatShortDate = (date) => {
    try {
        const d = date instanceof Date ? date : new Date(date);
        if (isNaN(d)) return '________';
        const dd = String(d.getDate()).padStart(2, '0');
        const mm = String(d.getMonth() + 1).padStart(2, '0');
        const yyyy = d.getFullYear();
        return `${dd}/${mm}/${yyyy}`;
    } catch {
        return '________';
    }
};

const calculateWeeks = (start, end) => {
    try {
        const s = new Date(start);
        const e = new Date(end);
        if (isNaN(s) || isNaN(e)) return '____';
        const diff = Math.abs(e - s);
        return Math.ceil(diff / (1000 * 60 * 60 * 24 * 7));
    } catch {
        return '____';
    }
};

// Styles
const styles = StyleSheet.create({
    page: {
        fontFamily: 'NotoSansDevanagari',
        padding: 60,
    },
    date: {
        fontSize: 11,
        marginBottom: 20,
        top: '8%',
    },
    title: {
        fontSize: 14,
        fontWeight: 'bold',
        textAlign: 'center',
        marginVertical: 10,
        textTransform: 'uppercase',
        textDecoration: 'underline',
    },
    body: {
        fontSize: 11,
        lineHeight: 1.6,
        textAlign: 'justify',
        marginTop: 10,
    },
    strong: {
        fontWeight: 'bold',
    },
    content: {
        marginTop: '15%',
    },
    signatureRow: {
        flexDirection: 'row',
        alignItems: 'flex-start',
        marginTop: 30,
        marginBottom: 120,
    },
    stamp: {
        width: 70,
        height: 70,
        marginRight: 12,
    },
    signatureBox: {
        flex: 1,
    },
    printedName: {
        fontSize: 11,
        fontWeight: 'bold',
        marginTop: 6,
    },
    printedTitle: {
        fontSize: 10,
        marginTop: 2,
    },
    signature: {
        width: 120,
        height: 40,
        marginTop: 6,
    },
    watermark: {
        position: 'absolute',
        top: '25%',
        left: '25%',
        width: '40%',
        opacity: 0.08,
    },
    pdfHeader: {
        position: 'absolute',
        top: '3%',
        left: '6%',
        width: '30%',
    },
    contactBlock: {
        position: 'absolute',
        left: 40,
        right: 40,
        bottom: 40,
        borderTop: '0.5pt solid #0b0b0b',
        paddingTop: 8,
        flexDirection: 'row',
        justifyContent: 'space-between',
    },
    contactText: {
        fontSize: 8,
    },
    websiteContainer: {
        position: 'absolute',
        left: 40,
        right: 40,
        bottom: 12,
        alignItems: 'center',
    },
    hr: {
        width: '100%',
        height: 0.5,
        backgroundColor: '#0b0b0b',
    },
    websiteText: {
        fontSize: 7,
        textAlign: 'center',
        marginTop: 4,
        color: '#333',
    },
});

// Component
const ExperienceCertificateIntern = ({
    employee = {},
    companyName = '',
    companyLogo,
    complete_logo,
    signatureImage,
    stampImage,
    signatoryName = '',
    signatoryTitle = '',
    companyAddress1 = '',
    companyAddress2 = '',
    contact = '',
    companyWebsite = '',
    issueDate = new Date(),
}) => {
    const {
        name = '',
        startDate = '',
        endDate = '',
        college = 'Government Polytechnic Mumbai',
    } = employee;

    return (
        <Document>
            <Page size="A4" style={styles.page}>
                {/* Date */}
                <Text style={styles.date}>{formatDate(issueDate)}</Text>

                {/* Watermark & Header */}
                {companyLogo && <Image src={companyLogo} style={styles.watermark} />}
                {complete_logo && <Image src={complete_logo} style={styles.pdfHeader} />}

                {/* Content */}
                <View style={styles.content}>
                    <Text style={styles.title}>
                        INTERNSHIP COMPLETION CERTIFICATE
                    </Text>

                    <Text style={styles.body}>
                        This is to certify that Ms./Mr.{' '}
                        <Text style={styles.strong}>{name || '________'}</Text>, a student from{' '}
                        <Text style={styles.strong}>{college}</Text>, has successfully completed
                        an internship in web development at{' '}
                        <Text style={styles.strong}>{companyName}</Text> from{' '}
                        <Text style={styles.strong}>{formatDate(startDate)}</Text> to{' '}
                        <Text style={styles.strong}>{formatDate(endDate)}</Text>, for a duration of{' '}
                        <Text style={styles.strong}>
                            {calculateWeeks(startDate, endDate)} Weeks
                        </Text>.
                    </Text>

                    <Text style={styles.body}>
                        During this period, the intern has undergone structured training followed
                        by a hands-on internship involving real-time tasks, projects, and evaluations.
                        The intern exhibited excellent performance, dedication, and a willingness
                        to learn and apply new skills.
                    </Text>

                    <Text style={styles.body}>
                        We acknowledge his/her contribution and wish him/her all the best for a
                        bright future.
                    </Text>

                    {/* Signature */}
                    <View style={styles.signatureRow}>
                        <View style={styles.signatureBox}>


                            {signatureImage && (
                                <Image src={signatureImage} style={styles.signature} />
                            )}

                            <Text style={styles.printedName}>{signatoryName}</Text>
                            <Text style={styles.printedName}>{signatoryTitle}</Text>
                            <Text style={styles.printedName}>{companyName}</Text>
                            <Text style={styles.printedTitle}>UDYAM-MH-26-0915549</Text>
                        </View>
                    </View>
                </View>

                {/* Footer */}
                <View style={styles.contactBlock}>
                    <View style={{ flex: 1 }}>
                        {companyAddress1 && (
                            <Text style={styles.contactText}>{companyAddress1}</Text>
                        )}
                        {companyAddress2 && (
                            <Text style={styles.contactText}>{companyAddress2}</Text>
                        )}
                    </View>

                    <View style={{ width: 200, textAlign: 'right' }}>
                        {contact &&
                            contact.split(/\r?\n|\|/).map((line, idx) => (
                                <Text key={idx} style={styles.contactText}>
                                    {line.trim()}
                                </Text>
                            ))}
                    </View>
                </View>

                {companyWebsite && (
                    <View style={styles.websiteContainer}>
                        <View style={styles.hr} />
                        <Text style={styles.websiteText}>{companyWebsite}</Text>
                    </View>
                )}
            </Page>
        </Document>
    );
};

export default ExperienceCertificateIntern;